/**
 * Normiz Compliance Intelligence Engine
 * Supabase Client & Query Helpers
 *
 * This module provides the Supabase client for frontend use and type-safe
 * query helpers for the compliance database.
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type {
  Database,
  State,
  County,
  City,
  Jurisdiction,
  TradeType,
  PermitType,
  JobScope,
  RequirementItem,
  RequirementItemWithRelations,
  ValidationRule,
  CheckRunInsert,
  CheckFindingInsert,
  CheckRun,
  CheckFinding,
  LicenseType,
  AttachmentCategory,
} from '@/integrations/supabase/types';
import type { FormTemplate, FormFieldMapping, FormSection, FormValidationRule } from '@/types/jurisdiction';

// ============================================
// CLIENT INITIALIZATION
// ============================================

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Warn if not configured (compliance features will be disabled)
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    '[Normiz] Supabase environment variables not configured. Compliance features will be disabled.'
  );
}

/**
 * Supabase client instance
 * Will be null if environment variables are not configured
 */
export const supabase: SupabaseClient<Database> | null =
  supabaseUrl && supabaseAnonKey
    ? createClient<Database>(supabaseUrl, supabaseAnonKey)
    : null;

/**
 * Check if Supabase is configured and available
 */
export const isSupabaseConfigured = (): boolean => supabase !== null;

// ============================================
// GEOGRAPHIC HIERARCHY QUERIES
// ============================================

/**
 * Get all states (currently just Florida for MVP)
 */
export async function getStates(): Promise<{ data: State[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('states')
    .select('*')
    .order('name');

  return { data: data || [], error: error as Error | null };
}

/**
 * Get Florida state record
 */
export async function getFloridaState(): Promise<{ data: State | null; error: Error | null }> {
  if (!supabase) return { data: null, error: null };

  const { data, error } = await supabase
    .from('states')
    .select('*')
    .eq('code', 'FL')
    .single();

  return { data, error: error as Error | null };
}

/**
 * Get all counties for a state
 */
export async function getCountiesByState(
  stateId: number
): Promise<{ data: County[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('counties')
    .select('*')
    .eq('state_id', stateId)
    .order('name');

  return { data: data || [], error: error as Error | null };
}

/**
 * Get all Florida counties (convenience method)
 */
export async function getFloridaCounties(): Promise<{ data: County[]; error: Error | null }> {
  const { data: florida } = await getFloridaState();
  if (!florida) return { data: [], error: null };
  return getCountiesByState(florida.id);
}

/**
 * Get all cities for a county
 */
export async function getCitiesByCounty(
  countyId: number
): Promise<{ data: City[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('cities')
    .select('*')
    .eq('county_id', countyId)
    .order('name');

  return { data: data || [], error: error as Error | null };
}

// ============================================
// JURISDICTION QUERIES
// ============================================

/**
 * Get jurisdiction by its unique code (e.g., 'FL-MIAMI-DADE-MIAMI')
 */
export async function getJurisdictionByCode(
  code: string
): Promise<{ data: Jurisdiction | null; error: Error | null }> {
  if (!supabase) return { data: null, error: null };

  const { data, error } = await supabase
    .from('jurisdictions')
    .select('*')
    .eq('code', code)
    .single();

  return { data, error: error as Error | null };
}

/**
 * Get jurisdiction by geographic location
 * Finds the most specific jurisdiction (city > county > state)
 */
export async function getJurisdictionByLocation(
  stateId: number,
  countyId?: number,
  cityId?: number
): Promise<{ data: Jurisdiction | null; error: Error | null }> {
  if (!supabase) return { data: null, error: null };

  let query = supabase.from('jurisdictions').select('*').eq('state_id', stateId);

  if (cityId) {
    // Try to find city-level jurisdiction
    query = query.eq('city_id', cityId);
  } else if (countyId) {
    // Try to find county-level jurisdiction (no city)
    query = query.eq('county_id', countyId).is('city_id', null);
  } else {
    // State-level only
    query = query.is('county_id', null).is('city_id', null);
  }

  const { data, error } = await query.single();
  return { data, error: error as Error | null };
}

/**
 * Get all jurisdictions for a state
 */
export async function getJurisdictionsByState(
  stateId: number
): Promise<{ data: Jurisdiction[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('jurisdictions')
    .select('*')
    .eq('state_id', stateId)
    .order('ahj_name');

  return { data: data || [], error: error as Error | null };
}

/**
 * Get all HVHZ (High Velocity Hurricane Zone) jurisdictions
 */
export async function getHVHZJurisdictions(): Promise<{
  data: Jurisdiction[];
  error: Error | null;
}> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('jurisdictions')
    .select('*')
    .eq('is_hvhz', true)
    .order('ahj_name');

  return { data: data || [], error: error as Error | null };
}

// ============================================
// TRADE & PERMIT TYPE QUERIES
// ============================================

/**
 * Get all trade types
 */
export async function getTradeTypes(): Promise<{ data: TradeType[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('trade_types')
    .select('*')
    .order('sort_order');

  return { data: data || [], error: error as Error | null };
}

/**
 * Get all permit types
 */
export async function getPermitTypes(): Promise<{ data: PermitType[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('permit_types')
    .select('*')
    .order('sort_order');

  return { data: data || [], error: error as Error | null };
}

/**
 * Get all job scopes, optionally filtered by trade type
 */
export async function getJobScopes(
  tradeTypeId?: number
): Promise<{ data: JobScope[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  let query = supabase.from('job_scopes').select('*');

  if (tradeTypeId) {
    query = query.eq('trade_type_id', tradeTypeId);
  }

  const { data, error } = await query.order('sort_order');
  return { data: data || [], error: error as Error | null };
}

/**
 * Get license types for a state and trade
 */
export async function getLicenseTypes(
  stateId: number,
  tradeTypeId?: number
): Promise<{ data: LicenseType[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  let query = supabase.from('license_types').select('*').eq('state_id', stateId);

  if (tradeTypeId) {
    query = query.eq('trade_type_id', tradeTypeId);
  }

  const { data, error } = await query.order('type_name');
  return { data: data || [], error: error as Error | null };
}

/**
 * Get attachment categories
 */
export async function getAttachmentCategories(): Promise<{
  data: AttachmentCategory[];
  error: Error | null;
}> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('attachment_categories')
    .select('*')
    .order('name');

  return { data: data || [], error: error as Error | null };
}

// ============================================
// REQUIREMENT ITEMS QUERIES (CORE COMPLIANCE)
// ============================================

/**
 * Get requirements for a specific jurisdiction and permit type
 * This is the core query for the compliance checklist
 */
export async function getRequirements(params: {
  jurisdictionId: number;
  permitTypeCode: string;
  jobScopeCode?: string;
}): Promise<{ data: RequirementItemWithRelations[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  // First get the permit type ID
  const { data: permitType } = await supabase
    .from('permit_types')
    .select('id')
    .eq('code', params.permitTypeCode)
    .single();

  if (!permitType) {
    return { data: [], error: new Error(`Permit type ${params.permitTypeCode} not found`) };
  }

  // Build the requirements query
  let query = supabase
    .from('requirement_items')
    .select(
      `
      *,
      attachment_categories (id, code, name, description, typical_formats),
      permit_types (id, code, name),
      job_scopes (id, code, name)
    `
    )
    .eq('permit_type_id', permitType.id)
    .or(`jurisdiction_id.eq.${params.jurisdictionId},jurisdiction_id.is.null`)
    .order('sort_order');

  // Filter by job scope if provided
  if (params.jobScopeCode) {
    const { data: jobScope } = await supabase
      .from('job_scopes')
      .select('id')
      .eq('code', params.jobScopeCode)
      .single();

    if (jobScope) {
      query = query.or(`job_scope_id.eq.${jobScope.id},job_scope_id.is.null`);
    }
  }

  const { data, error } = await query;
  return { data: (data as RequirementItemWithRelations[]) || [], error: error as Error | null };
}

/**
 * Get all requirement items for a jurisdiction (all permit types)
 */
export async function getAllRequirementsForJurisdiction(
  jurisdictionId: number
): Promise<{ data: RequirementItemWithRelations[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('requirement_items')
    .select(
      `
      *,
      attachment_categories (id, code, name, description),
      permit_types (id, code, name),
      job_scopes (id, code, name)
    `
    )
    .or(`jurisdiction_id.eq.${jurisdictionId},jurisdiction_id.is.null`)
    .order('sort_order');

  return { data: (data as RequirementItemWithRelations[]) || [], error: error as Error | null };
}

// ============================================
// VALIDATION RULES QUERIES
// ============================================

/**
 * Get all active validation rules
 */
export async function getValidationRules(): Promise<{
  data: ValidationRule[];
  error: Error | null;
}> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('validation_rules')
    .select('*')
    .eq('is_active', true)
    .order('rule_code');

  return { data: data || [], error: error as Error | null };
}

/**
 * Get validation rules for specific requirement items
 */
export async function getValidationRulesForRequirements(
  requirementItemIds: number[]
): Promise<{ data: ValidationRule[]; error: Error | null }> {
  if (!supabase || requirementItemIds.length === 0) return { data: [], error: null };

  const { data, error } = await supabase
    .from('requirement_validation_rules')
    .select(
      `
      validation_rules (*)
    `
    )
    .in('requirement_item_id', requirementItemIds);

  // Extract the validation rules from the joined result
  const rules = data?.map((r) => r.validation_rules).filter(Boolean) as ValidationRule[];
  return { data: rules || [], error: error as Error | null };
}

// ============================================
// AUDIT TRAIL QUERIES
// ============================================

/**
 * Log a compliance check run
 */
export async function logCheckRun(
  checkRun: CheckRunInsert
): Promise<{ data: CheckRun | null; error: Error | null }> {
  if (!supabase) return { data: null, error: null };

  const { data, error } = await supabase
    .from('check_runs')
    .insert(checkRun)
    .select()
    .single();

  return { data, error: error as Error | null };
}

/**
 * Log a compliance finding
 */
export async function logFinding(
  finding: CheckFindingInsert
): Promise<{ data: CheckFinding | null; error: Error | null }> {
  if (!supabase) return { data: null, error: null };

  const { data, error } = await supabase
    .from('check_findings')
    .insert(finding)
    .select()
    .single();

  return { data, error: error as Error | null };
}

/**
 * Get check runs for a job
 */
export async function getCheckRunsForJob(
  jobId: string
): Promise<{ data: CheckRun[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('check_runs')
    .select('*')
    .eq('job_id', jobId)
    .order('created_at', { ascending: false });

  return { data: data || [], error: error as Error | null };
}

/**
 * Get findings for a check run
 */
export async function getFindingsForCheckRun(
  checkRunId: string
): Promise<{ data: CheckFinding[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('check_findings')
    .select('*')
    .eq('check_run_id', checkRunId)
    .order('severity');

  return { data: data || [], error: error as Error | null };
}

/**
 * Get contractor's recent check runs
 */
export async function getContractorCheckRuns(
  contractorId: string,
  limit = 10
): Promise<{ data: CheckRun[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('check_runs')
    .select('*')
    .eq('contractor_id', contractorId)
    .order('created_at', { ascending: false })
    .limit(limit);

  return { data: data || [], error: error as Error | null };
}

// ============================================
// FORM TEMPLATES QUERIES
// ============================================

/**
 * Database row type for form_templates
 */
interface FormTemplateRow {
  id: string;
  jurisdiction_id: number | null;
  jurisdiction_code: string;
  jurisdiction_name: string;
  permit_type: string;
  form_name: string;
  version: string;
  last_updated: string;
  template_url: string | null;
  source_url: string | null;
  field_mappings: FormFieldMapping[];
  validations: FormValidationRule[];
  sections: FormSection[];
  active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Convert database row to FormTemplate type
 */
function mapRowToFormTemplate(row: FormTemplateRow): FormTemplate {
  return {
    id: row.id,
    jurisdictionId: row.jurisdiction_id ?? 0,
    jurisdictionCode: row.jurisdiction_code,
    jurisdictionName: row.jurisdiction_name,
    permitType: row.permit_type as FormTemplate['permitType'],
    formName: row.form_name,
    version: row.version,
    lastUpdated: row.last_updated,
    templateUrl: row.template_url ?? '',
    sourceUrl: row.source_url ?? undefined,
    fieldMappings: row.field_mappings,
    validations: row.validations,
    sections: row.sections,
    active: row.active,
    notes: row.notes ?? undefined,
  };
}

/**
 * Get all form templates for a jurisdiction
 */
export async function getFormTemplatesByJurisdiction(
  jurisdictionCode: string,
  permitType?: string
): Promise<{ data: FormTemplate[]; error: Error | null }> {
  if (!supabase) return { data: [], error: null };

  let query = supabase
    .from('form_templates')
    .select('*')
    .eq('jurisdiction_code', jurisdictionCode)
    .eq('active', true);

  if (permitType) {
    query = query.eq('permit_type', permitType);
  }

  const { data, error } = await query.order('permit_type').order('form_name');

  if (error) {
    return { data: [], error: error as Error };
  }

  const templates = (data as FormTemplateRow[])?.map(mapRowToFormTemplate) || [];
  return { data: templates, error: null };
}

/**
 * Get a specific form template by ID
 */
export async function getFormTemplateById(
  templateId: string
): Promise<{ data: FormTemplate | null; error: Error | null }> {
  if (!supabase) return { data: null, error: null };

  const { data, error } = await supabase
    .from('form_templates')
    .select('*')
    .eq('id', templateId)
    .single();

  if (error) {
    return { data: null, error: error as Error };
  }

  return { data: mapRowToFormTemplate(data as FormTemplateRow), error: null };
}

/**
 * Get all active form templates
 */
export async function getAllFormTemplates(): Promise<{
  data: FormTemplate[];
  error: Error | null;
}> {
  if (!supabase) return { data: [], error: null };

  const { data, error } = await supabase
    .from('form_templates')
    .select('*')
    .eq('active', true)
    .order('jurisdiction_name')
    .order('permit_type');

  if (error) {
    return { data: [], error: error as Error };
  }

  const templates = (data as FormTemplateRow[])?.map(mapRowToFormTemplate) || [];
  return { data: templates, error: null };
}

// ============================================
// CONVENIENCE EXPORTS
// ============================================

/**
 * Compliance database query helpers
 * All queries return { data, error } format
 */
export const complianceDb = {
  // Geographic
  getStates,
  getFloridaState,
  getCountiesByState,
  getFloridaCounties,
  getCitiesByCounty,

  // Jurisdictions
  getJurisdictionByCode,
  getJurisdictionByLocation,
  getJurisdictionsByState,
  getHVHZJurisdictions,

  // Trade/Permit Types
  getTradeTypes,
  getPermitTypes,
  getJobScopes,
  getLicenseTypes,
  getAttachmentCategories,

  // Requirements (core)
  getRequirements,
  getAllRequirementsForJurisdiction,

  // Validation
  getValidationRules,
  getValidationRulesForRequirements,

  // Audit Trail
  logCheckRun,
  logFinding,
  getCheckRunsForJob,
  getFindingsForCheckRun,
  getContractorCheckRuns,

  // Form Templates
  getFormTemplatesByJurisdiction,
  getFormTemplateById,
  getAllFormTemplates,
};

export default supabase;
